import { Link } from 'react-router-dom';

export function FAQPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 transition-colors duration-300 font-medium inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Directory
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <article className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>

          <div className="prose max-w-none space-y-8">
            {/* General Questions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b-2 border-blue-500 pb-2">General Questions</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What is The Definitive D8 Directory?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Definitive D8 Directory is a comprehensive, research-backed resource provided by Alpha Research
                    that ranks and evaluates online vendors of hemp-derived cannabinoid products, including Delta-8 THC,
                    CBD, CBG, and other compounds. We analyzed 260+ sources to identify and rank the top 47 vendors
                    based on a rigorous 6-criteria methodology.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">How are vendors ranked?</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Each vendor is scored on a 10-point scale across six weighted criteria:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li><strong>Product Catalog (20%):</strong> Diversity of cannabinoids and product formats</li>
                    <li><strong>Trustworthiness & Transparency (25%):</strong> Website security, COA accessibility</li>
                    <li><strong>Consumer Reviews (25%):</strong> Aggregate ratings and sentiment analysis</li>
                    <li><strong>Company History (10%):</strong> Years in operation, brand reputation</li>
                    <li><strong>Market Share (5%):</strong> Publicly available sales data and indicators</li>
                    <li><strong>Customer Service (15%):</strong> Return policies, shipping, responsiveness</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    See our <Link to="/editor" className="text-blue-600 hover:underline">full research report</Link> for detailed methodology.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Do you sell products or receive commissions from vendors?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    No. We do not sell products, and vendor rankings are not influenced by affiliate relationships or
                    commissions. Our rankings are based solely on objective research criteria. We are an independent
                    informational resource operated by Alpha Research.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">How often is the directory updated?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We review and update vendor information quarterly. However, vendor details (products, policies,
                    availability) may change between updates. Always verify information directly with vendors before
                    making purchases.
                  </p>
                </div>
              </div>
            </section>

            {/* Legal & Safety */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b-2 border-blue-500 pb-2">Legal & Safety</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Is Delta-8 THC legal?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Delta-8 THC exists in a legal gray area. The 2018 Farm Bill legalized hemp and its derivatives,
                    but the DEA maintains that synthetically derived THC remains illegal. Several states have banned
                    Delta-8 explicitly, while others regulate it. <strong>You are responsible for verifying legality
                    in your jurisdiction before purchasing or possessing these products.</strong>
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Which states have banned Delta-8 THC?</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    As of our last update, states with Delta-8 bans or restrictions include (but are not limited to):
                  </p>
                  <p className="text-gray-700 font-medium">
                    Alaska, Arizona, Arkansas, Colorado, Delaware, Idaho, Iowa, Kentucky, Mississippi, Montana,
                    New York, North Dakota, Rhode Island, Utah, Vermont, Washington
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2 italic">
                    State laws change frequently. Always check current regulations in your state and locality.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Will Delta-8 THC show up on a drug test?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Yes. Delta-8 THC metabolizes similarly to Delta-9 THC, and standard drug tests do not distinguish
                    between them. If you are subject to drug testing, assume Delta-8 products will cause a positive result.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Are these products safe?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Product safety depends on manufacturing quality and third-party testing. Our directory prioritizes
                    vendors with accessible Certificates of Analysis (COAs) showing purity and potency testing. However,
                    the hemp cannabinoid industry is largely unregulated. <strong>Always review COAs and consult a
                    healthcare provider before using these products.</strong>
                  </p>
                </div>
              </div>
            </section>

            {/* Using the Directory */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b-2 border-blue-500 pb-2">Using the Directory</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">How do I search for specific vendors?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Use the search bar on the main directory page to filter by vendor name or ranking number.
                    You can also click on vendor cards to see detailed information including product categories,
                    rankings, and direct website links.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What's the difference between vendors ranked 1-10 vs. 40-47?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Top-ranked vendors (1-10) excel across multiple criteria: comprehensive product catalogs, transparent
                    testing, exceptional customer service, and strong brand reputation. Lower-ranked vendors may have
                    limited product selection, restrictive return policies, or less transparent business practices. All
                    47 vendors met our baseline validation requirements for inclusion.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Can I submit a vendor for consideration?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We accept vendor submissions for quarterly review cycles. Email us at{' '}
                    <a href="mailto:leverage_labs_alpha@proton.me" className="text-blue-600 hover:underline">
                      leverage_labs_alpha@proton.me
                    </a>{' '}
                    with the vendor name, website, and brief description. Vendors must meet our validation criteria to
                    be considered for ranking.
                  </p>
                </div>
              </div>
            </section>

            {/* Products & Terminology */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b-2 border-blue-500 pb-2">Products & Terminology</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What is Delta-8 THC?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Delta-8 THC (Delta-8-tetrahydrocannabinol) is a minor cannabinoid found in hemp. It's chemically
                    similar to Delta-9 THC (the primary psychoactive compound in marijuana) but is reported to produce
                    milder effects. Delta-8 is typically produced by converting CBD through chemical processes.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What's the difference between full-spectrum, broad-spectrum, and isolate?</h3>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                    <li><strong>Full-Spectrum:</strong> Contains all naturally occurring cannabinoids, including trace
                    amounts of Delta-9 THC (≤0.3%)</li>
                    <li><strong>Broad-Spectrum:</strong> Contains multiple cannabinoids but with Delta-9 THC removed</li>
                    <li><strong>Isolate:</strong> Pure cannabinoid (99%+) with all other compounds removed</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What are Certificates of Analysis (COAs)?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    COAs are third-party lab reports showing a product's cannabinoid content, purity, and absence of
                    contaminants (pesticides, heavy metals, solvents). Top vendors provide batch-specific, up-to-date
                    COAs accessible on product pages. Always review COAs before purchasing.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">What is THCa, and how is it different from Delta-8?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    THCa (tetrahydrocannabinolic acid) is the non-psychoactive precursor to Delta-9 THC found in raw hemp
                    flower. When heated (smoked or vaped), THCa converts to psychoactive Delta-9 THC, making THCa flower
                    functionally identical to marijuana. Delta-8 is a distinct cannabinoid that doesn't require heating
                    for effects.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact & Support */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b-2 border-blue-500 pb-2">Contact & Support</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">How can I contact Alpha Research?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Email us at: <a href="mailto:leverage_labs_alpha@proton.me" className="text-blue-600 hover:underline">
                      leverage_labs_alpha@proton.me
                    </a>
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    We respond to inquiries within 2-3 business days.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">I found incorrect vendor information. How do I report it?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Please email details to <a href="mailto:leverage_labs_alpha@proton.me" className="text-blue-600 hover:underline">
                      leverage_labs_alpha@proton.me
                    </a> including the vendor name, incorrect information, and any supporting documentation. We review
                    all corrections promptly.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">Do you offer consulting or custom research services?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Alpha Research offers custom market research and analysis services for businesses in the hemp
                    cannabinoid industry. Contact us at{' '}
                    <a href="mailto:leverage_labs_alpha@proton.me" className="text-blue-600 hover:underline">
                      leverage_labs_alpha@proton.me
                    </a> for inquiries.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Back to Directory CTA */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-md hover:shadow-lg"
            >
              Back to Vendor Directory
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
